import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { BalanceService, ErrorStateService, VoucherRedeemModel, VoucherService, config } from '@fgb/core';
import { firstValueFrom } from 'rxjs';

export type VoucherStatus = 'success' | 'invalid' | 'expired' | 'used' | 'empty' | null;

type StatusVariant = 'success' | 'invalid' | 'neutral';

interface StatusConfig {

  key: string;
  fgbdefault: string;
  icon: string;
  variant: StatusVariant;
}

const STATUS_CONFIG: Record<NonNullable<VoucherStatus>, StatusConfig> = {
  success: {
    key: 'voucher.success-voucher',
    fgbdefault: 'Applied Successfully!',
    icon: 'check_circle',
    variant: 'success',
  },
  invalid: {
    key: 'voucher.invalid-code',
    fgbdefault: 'Invalid Code. Please try again',
    icon: 'cancel',
    variant: 'invalid',
  },
  expired: {
    key: 'voucher.expired-code',
    fgbdefault: 'Sorry, this code has expired',
    icon: 'schedule',
    variant: 'neutral',
  },
  used: {
    key: 'voucher.used-code',
    fgbdefault: 'This code has already been used',
    icon: 'block',
    variant: 'neutral',
  },
  empty: {
    key: 'voucher.empty-code',
    fgbdefault: 'Voucher code is empty',
    icon: 'warning',
    variant: 'neutral',
  },
};

const ERROR_PATTERNS: { pattern: RegExp; status: VoucherStatus }[] = [
  { pattern: /already.*(been|redeemed)|redeemed/i, status: 'used' },
  { pattern: /expired|no longer active/i, status: 'expired' },
];

const AUTO_CLEAR_MS = 3000;

@Component({
  selector: 'fgb-vouchers',
  templateUrl: './vouchers.component.html',
  styleUrls: ['./vouchers.component.scss'],
})
export class VouchersComponent {
  private voucherService = inject(VoucherService);
  private balanceService = inject(BalanceService);
  private errorService = inject(ErrorStateService);
  private destroyRef = inject(DestroyRef);

  voucherCode = '';
  isLoading = signal(false);
  status = signal<VoucherStatus>(null);

  statusKey = computed(() => (this.status() ? STATUS_CONFIG[this.status()!].key : ''));
  statusDefault = computed(() => (this.status() ? STATUS_CONFIG[this.status()!].fgbdefault : ''));
  statusIcon = computed(() => (this.status() ? STATUS_CONFIG[this.status()!].icon : ''));
  variantType = computed(() => (this.status() ? STATUS_CONFIG[this.status()!].variant : ''));
  isInvalid = computed(() => !!this.status() && this.status() !== 'success');

  private statusTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.clearStatusTimer());
  }

  async redeemVoucher(): Promise<void> {
    const code = this.voucherCode.trim();

    if (!code) {
      this.setStatus('empty');
      return;
    }

    this.isLoading.set(true);
    this.status.set(null);
    this.errorService.clearErrors();

    const voucher = new VoucherRedeemModel();
    voucher.VoucherCode = code;

    try {
      await firstValueFrom(this.voucherService.redeemVoucher(voucher));
      await firstValueFrom(this.balanceService.fetchBalance(config.purseConfig.virtualLoyalty));
    } catch {

    } finally {
      this.isLoading.set(false);
      const errorMsg = this.errorService.errors?.[0] ?? null;

      if (!this.errorService.hasErrors() || !errorMsg) {
        this.voucherCode = '';
        this.setStatus('success');
      } else {
        this.setStatus(this.resolveErrorStatus(errorMsg));
      }

      this.errorService.clearErrors();
    }
  }

  private resolveErrorStatus(message: string): VoucherStatus {
    for (const { pattern, status } of ERROR_PATTERNS) {
      if (pattern.test(message)) return status;
    }
    return 'invalid';
  }

  private setStatus(status: VoucherStatus): void {
    this.clearStatusTimer();
    this.status.set(status);
    this.statusTimer = setTimeout(() => this.status.set(null), AUTO_CLEAR_MS);
  }

  private clearStatusTimer(): void {
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
      this.statusTimer = null;
    }
  }
}
